import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';
import { NodeValidationError } from '@modules/campaigns/Domain/Interfaces/NodeValidationError';

interface EnrichedNodeErrorGroup {
  typeLabel: string;
  errors: string[];
}

const NODE_TYPE_LABELS: Record<string, string> = {
  start: 'Start',
  end: 'End',
  sendSMS: 'Send SMS',
  sendEmail: 'Send Email',
  sendCertifiedSMS: 'Certified SMS',
  sendCertifiedEmail: 'Certified Email',
  paylinkNotificationStatus: 'Conditional',
  customerCaseSegments: 'Segmented Conditional',
  waitDays: 'Wait Days',
  waitDayOfMonth: 'Wait Day of Month',
  waitDayOfWeek: 'Wait Day of Week',
  waitDaysForDueDate: 'Wait Days for Due Date',
  assignCampaign: 'Assign Campaign',
  offerPaymentAgreement: 'Payment Agreement',
  offerPartialPayment: 'Partial Payment',
  makeOutboundCall: 'Outbound Call',
  createLegalClaim: 'Legal Claim',
  createMasc: 'Create MASC',
  experiment: 'A/B Test',
  multiconditional: 'Multi-Conditional',
};

@Component({
  selector: 'app-workflow-validation-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonModule, DialogModule],
  templateUrl: './workflow-validation-dialog.component.html',
})
export class WorkflowValidationDialogComponent implements OnChanges {
  @Input() set visible(v: boolean) {
    this._visible = v;
  }
  @Input() workflowErrors: string[] = [];
  @Input() nodeErrors: NodeValidationError[] = [];
  @Input() nodes: NodeClass[] = [];
  /** 'warn' shows a "Save anyway" button; 'error' only allows closing */
  @Input() severity: 'warn' | 'error' = 'warn';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saveAnyway = new EventEmitter<void>();

  _visible = false;
  enrichedNodeGroups: EnrichedNodeErrorGroup[] = [];

  ngOnChanges(): void {
    this.enrichedNodeGroups = this.buildEnrichedGroups();
  }

  get totalIssues(): number {
    return this.workflowErrors.length + this.nodeErrors.length;
  }

  close(): void {
    this._visible = false;
    this.visibleChange.emit(false);
  }

  onSaveAnyway(): void {
    this.close();
    this.saveAnyway.emit();
  }

  private buildEnrichedGroups(): EnrichedNodeErrorGroup[] {
    const map = new Map<string, EnrichedNodeErrorGroup>();

    for (const err of this.nodeErrors) {
      if (!map.has(err.nodeId)) {
        const node = this.nodes.find((n) => n.id === err.nodeId);
        map.set(err.nodeId, {
          typeLabel: node ? (NODE_TYPE_LABELS[node.type] ?? node.type) : `Node ${err.nodeId}`,
          errors: [],
        });
      }
      if (err.errorMessage) {
        map.get(err.nodeId)!.errors.push(err.errorMessage);
      }
    }

    return Array.from(map.values());
  }
}